/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `bike_profile` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class BikeProfileMesg : Mesg {
    public constructor() : super("BikeProfile", Profile.MesgNum.BIKE_PROFILE)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** name. */
    public var name: String?
        get() = getFieldValue(NAME_FIELD_NUM) as? String
        set(value) = setFieldValue(NAME_FIELD_NUM, value)

    /** sport. */
    public var sport: Sport?
        get() = (getFieldValue(SPORT_FIELD_NUM) as? UByte)?.let { Sport.fromValue(it) }
        set(value) = setFieldValue(SPORT_FIELD_NUM, value?.value)

    /** sub_sport. */
    public var subSport: SubSport?
        get() = (getFieldValue(SUB_SPORT_FIELD_NUM) as? UByte)?.let { SubSport.fromValue(it) }
        set(value) = setFieldValue(SUB_SPORT_FIELD_NUM, value?.value)

    /** odometer, in m. */
    public var odometer: Double?
        get() = getFieldValue(ODOMETER_FIELD_NUM) as? Double
        set(value) = setFieldValue(ODOMETER_FIELD_NUM, value)

    /** bike_spd_ant_id. */
    public var bikeSpdAntId: UShort?
        get() = getFieldValue(BIKE_SPD_ANT_ID_FIELD_NUM) as? UShort
        set(value) = setFieldValue(BIKE_SPD_ANT_ID_FIELD_NUM, value)

    /** bike_cad_ant_id. */
    public var bikeCadAntId: UShort?
        get() = getFieldValue(BIKE_CAD_ANT_ID_FIELD_NUM) as? UShort
        set(value) = setFieldValue(BIKE_CAD_ANT_ID_FIELD_NUM, value)

    /** bike_spdcad_ant_id. */
    public var bikeSpdcadAntId: UShort?
        get() = getFieldValue(BIKE_SPDCAD_ANT_ID_FIELD_NUM) as? UShort
        set(value) = setFieldValue(BIKE_SPDCAD_ANT_ID_FIELD_NUM, value)

    /** bike_power_ant_id. */
    public var bikePowerAntId: UShort?
        get() = getFieldValue(BIKE_POWER_ANT_ID_FIELD_NUM) as? UShort
        set(value) = setFieldValue(BIKE_POWER_ANT_ID_FIELD_NUM, value)

    /** custom_wheelsize, in m. */
    public var customWheelsize: Double?
        get() = getFieldValue(CUSTOM_WHEELSIZE_FIELD_NUM) as? Double
        set(value) = setFieldValue(CUSTOM_WHEELSIZE_FIELD_NUM, value)

    /** auto_wheelsize, in m. */
    public var autoWheelsize: Double?
        get() = getFieldValue(AUTO_WHEELSIZE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AUTO_WHEELSIZE_FIELD_NUM, value)

    /** bike_weight, in kg. */
    public var bikeWeight: Double?
        get() = getFieldValue(BIKE_WEIGHT_FIELD_NUM) as? Double
        set(value) = setFieldValue(BIKE_WEIGHT_FIELD_NUM, value)

    /** power_cal_factor, in %. */
    public var powerCalFactor: Double?
        get() = getFieldValue(POWER_CAL_FACTOR_FIELD_NUM) as? Double
        set(value) = setFieldValue(POWER_CAL_FACTOR_FIELD_NUM, value)

    /** auto_wheel_cal. */
    public var autoWheelCal: Boolean?
        get() = (getFieldValue(AUTO_WHEEL_CAL_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(AUTO_WHEEL_CAL_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** auto_power_zero. */
    public var autoPowerZero: Boolean?
        get() = (getFieldValue(AUTO_POWER_ZERO_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(AUTO_POWER_ZERO_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** id. */
    public var id: UByte?
        get() = getFieldValue(ID_FIELD_NUM) as? UByte
        set(value) = setFieldValue(ID_FIELD_NUM, value)

    /** spd_enabled. */
    public var spdEnabled: Boolean?
        get() = (getFieldValue(SPD_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(SPD_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** cad_enabled. */
    public var cadEnabled: Boolean?
        get() = (getFieldValue(CAD_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(CAD_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** spdcad_enabled. */
    public var spdcadEnabled: Boolean?
        get() = (getFieldValue(SPDCAD_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(SPDCAD_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** power_enabled. */
    public var powerEnabled: Boolean?
        get() = (getFieldValue(POWER_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(POWER_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** crank_length, in mm. */
    public var crankLength: Double?
        get() = getFieldValue(CRANK_LENGTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(CRANK_LENGTH_FIELD_NUM, value)

    /** enabled. */
    public var enabled: Boolean?
        get() = (getFieldValue(ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** bike_spd_ant_id_trans_type. */
    public var bikeSpdAntIdTransType: UByte?
        get() = getFieldValue(BIKE_SPD_ANT_ID_TRANS_TYPE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(BIKE_SPD_ANT_ID_TRANS_TYPE_FIELD_NUM, value)

    /** bike_cad_ant_id_trans_type. */
    public var bikeCadAntIdTransType: UByte?
        get() = getFieldValue(BIKE_CAD_ANT_ID_TRANS_TYPE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(BIKE_CAD_ANT_ID_TRANS_TYPE_FIELD_NUM, value)

    /** bike_spdcad_ant_id_trans_type. */
    public var bikeSpdcadAntIdTransType: UByte?
        get() = getFieldValue(BIKE_SPDCAD_ANT_ID_TRANS_TYPE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(BIKE_SPDCAD_ANT_ID_TRANS_TYPE_FIELD_NUM, value)

    /** bike_power_ant_id_trans_type. */
    public var bikePowerAntIdTransType: UByte?
        get() = getFieldValue(BIKE_POWER_ANT_ID_TRANS_TYPE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(BIKE_POWER_ANT_ID_TRANS_TYPE_FIELD_NUM, value)

    /** odometer_rollover. Rollover counter that can be used to extend the odometer */
    public var odometerRollover: UByte?
        get() = getFieldValue(ODOMETER_ROLLOVER_FIELD_NUM) as? UByte
        set(value) = setFieldValue(ODOMETER_ROLLOVER_FIELD_NUM, value)

    /** front_gear_num. Number of front gears */
    public var frontGearNum: UByte?
        get() = getFieldValue(FRONT_GEAR_NUM_FIELD_NUM) as? UByte
        set(value) = setFieldValue(FRONT_GEAR_NUM_FIELD_NUM, value)

    /** Number of values stored in front_gear. */
    public val numFrontGear: Int
        get() = getField(FRONT_GEAR_FIELD_NUM)?.numValues ?: 0

    /** front_gear. Number of teeth on each gear 0 is innermost */
    public var frontGear: List<UByte?>?
        get() {
            val field = getField(FRONT_GEAR_FIELD_NUM) ?: return null
            return List(field.numValues) { getFrontGear(it) }
        }
        set(value) {
            removeField(FRONT_GEAR_FIELD_NUM)
            value?.forEachIndexed { index, element -> setFrontGear(index, element) }
        }

    public fun getFrontGear(index: Int): UByte? =
        getFieldValue(FRONT_GEAR_FIELD_NUM, index) as? UByte

    public fun setFrontGear(index: Int, value: UByte?) {
        setFieldValue(FRONT_GEAR_FIELD_NUM, value, index)
    }

    /** rear_gear_num. Number of rear gears */
    public var rearGearNum: UByte?
        get() = getFieldValue(REAR_GEAR_NUM_FIELD_NUM) as? UByte
        set(value) = setFieldValue(REAR_GEAR_NUM_FIELD_NUM, value)

    /** Number of values stored in rear_gear. */
    public val numRearGear: Int
        get() = getField(REAR_GEAR_FIELD_NUM)?.numValues ?: 0

    /** rear_gear. Number of teeth on each gear 0 is innermost */
    public var rearGear: List<UByte?>?
        get() {
            val field = getField(REAR_GEAR_FIELD_NUM) ?: return null
            return List(field.numValues) { getRearGear(it) }
        }
        set(value) {
            removeField(REAR_GEAR_FIELD_NUM)
            value?.forEachIndexed { index, element -> setRearGear(index, element) }
        }

    public fun getRearGear(index: Int): UByte? =
        getFieldValue(REAR_GEAR_FIELD_NUM, index) as? UByte

    public fun setRearGear(index: Int, value: UByte?) {
        setFieldValue(REAR_GEAR_FIELD_NUM, value, index)
    }

    /** shimano_di2_enabled. */
    public var shimanoDi2Enabled: Boolean?
        get() = (getFieldValue(SHIMANO_DI2_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(SHIMANO_DI2_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val NAME_FIELD_NUM: UByte = 0u
        public const val SPORT_FIELD_NUM: UByte = 1u
        public const val SUB_SPORT_FIELD_NUM: UByte = 2u
        public const val ODOMETER_FIELD_NUM: UByte = 3u
        public const val BIKE_SPD_ANT_ID_FIELD_NUM: UByte = 4u
        public const val BIKE_CAD_ANT_ID_FIELD_NUM: UByte = 5u
        public const val BIKE_SPDCAD_ANT_ID_FIELD_NUM: UByte = 6u
        public const val BIKE_POWER_ANT_ID_FIELD_NUM: UByte = 7u
        public const val CUSTOM_WHEELSIZE_FIELD_NUM: UByte = 8u
        public const val AUTO_WHEELSIZE_FIELD_NUM: UByte = 9u
        public const val BIKE_WEIGHT_FIELD_NUM: UByte = 10u
        public const val POWER_CAL_FACTOR_FIELD_NUM: UByte = 11u
        public const val AUTO_WHEEL_CAL_FIELD_NUM: UByte = 12u
        public const val AUTO_POWER_ZERO_FIELD_NUM: UByte = 13u
        public const val ID_FIELD_NUM: UByte = 14u
        public const val SPD_ENABLED_FIELD_NUM: UByte = 15u
        public const val CAD_ENABLED_FIELD_NUM: UByte = 16u
        public const val SPDCAD_ENABLED_FIELD_NUM: UByte = 17u
        public const val POWER_ENABLED_FIELD_NUM: UByte = 18u
        public const val CRANK_LENGTH_FIELD_NUM: UByte = 19u
        public const val ENABLED_FIELD_NUM: UByte = 20u
        public const val BIKE_SPD_ANT_ID_TRANS_TYPE_FIELD_NUM: UByte = 21u
        public const val BIKE_CAD_ANT_ID_TRANS_TYPE_FIELD_NUM: UByte = 22u
        public const val BIKE_SPDCAD_ANT_ID_TRANS_TYPE_FIELD_NUM: UByte = 23u
        public const val BIKE_POWER_ANT_ID_TRANS_TYPE_FIELD_NUM: UByte = 24u
        public const val ODOMETER_ROLLOVER_FIELD_NUM: UByte = 37u
        public const val FRONT_GEAR_NUM_FIELD_NUM: UByte = 38u
        public const val FRONT_GEAR_FIELD_NUM: UByte = 39u
        public const val REAR_GEAR_NUM_FIELD_NUM: UByte = 40u
        public const val REAR_GEAR_FIELD_NUM: UByte = 41u
        public const val SHIMANO_DI2_ENABLED_FIELD_NUM: UByte = 44u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[0] = Field("Name", 0u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[1] = Field("Sport", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[2] = Field("SubSport", 2u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[3] = Field("Odometer", 3u, BaseType.UINT32, 100.0, 0.0, "m", false)
            fields[4] = Field("BikeSpdAntId", 4u, BaseType.UINT16Z, 1.0, 0.0, "", false)
            fields[5] = Field("BikeCadAntId", 5u, BaseType.UINT16Z, 1.0, 0.0, "", false)
            fields[6] = Field("BikeSpdcadAntId", 6u, BaseType.UINT16Z, 1.0, 0.0, "", false)
            fields[7] = Field("BikePowerAntId", 7u, BaseType.UINT16Z, 1.0, 0.0, "", false)
            fields[8] = Field("CustomWheelsize", 8u, BaseType.UINT16, 1000.0, 0.0, "m", false)
            fields[9] = Field("AutoWheelsize", 9u, BaseType.UINT16, 1000.0, 0.0, "m", false)
            fields[10] = Field("BikeWeight", 10u, BaseType.UINT16, 10.0, 0.0, "kg", false)
            fields[11] = Field("PowerCalFactor", 11u, BaseType.UINT16, 10.0, 0.0, "%", false)
            fields[12] = Field("AutoWheelCal", 12u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[13] = Field("AutoPowerZero", 13u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[14] = Field("Id", 14u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[15] = Field("SpdEnabled", 15u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[16] = Field("CadEnabled", 16u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[17] = Field("SpdcadEnabled", 17u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[18] = Field("PowerEnabled", 18u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[19] = Field("CrankLength", 19u, BaseType.UINT8, 2.0, -110.0, "mm", false)
            fields[20] = Field("Enabled", 20u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[21] = Field("BikeSpdAntIdTransType", 21u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[22] = Field("BikeCadAntIdTransType", 22u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[23] = Field("BikeSpdcadAntIdTransType", 23u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[24] = Field("BikePowerAntIdTransType", 24u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[37] = Field("OdometerRollover", 37u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[38] = Field("FrontGearNum", 38u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[39] = Field("FrontGear", 39u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[40] = Field("RearGearNum", 40u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[41] = Field("RearGear", 41u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[44] = Field("ShimanoDi2Enabled", 44u, BaseType.ENUM, 1.0, 0.0, "", false)
            return fields
        }
    }
}
